import { useReducer, useCallback, useRef, useEffect, useState } from "react";
import { isBillingLlmBlockedError } from "~/features/billing/types";
import { logger } from "~/lib/logger";

// AIDEV-NOTE: Modular architecture - types, reducer, and controllers separated for testability
import type {
    StreamingMeditationParams,
    StreamingMeditationState,
    StreamingMeditationActions,
    SSEEvent,
    MeditationPlaybackBreakType,
    MeditationPlaybackBreakLogData,
    MeditationPlaybackSessionContext,
} from "./streaming-meditation/types";
import {
    reducer,
    initialState,
    getStatusLabel,
    getTotalDuration,
} from "./streaming-meditation/reducer";
import { AudioController } from "./streaming-meditation/audio-controller";
import { useSSEConnection } from "./streaming-meditation/use-sse-connection";
import {
    meditationDebugLog,
    meditationDebugWarn,
    meditationVerboseDebugLog,
} from "~/features/meditations/utils/browser-debug";
import {
    MEDITATION_HLS_BOOTSTRAP_RETRY_DELAYS_MS,
    isRetryableHlsBootstrapResponse,
} from "~/features/meditations/utils/hls-bootstrap-retry";

interface HlsBootstrapPayload {
    playbackType: "hls" | "direct";
    playbackUrl: string;
    audibleStartOffsetSeconds?: number;
    // AIDEV-NOTE: Row id of the live generation. Keys the playback-end beacon
    // so the server can persist initialGenerationOutcome (staging 2026-06-10:
    // outcomes were never populated for streaming meditations without it).
    meditationId?: string | null;
    message?: string;
    // AIDEV-NOTE: BACKEND-368 — set on the reusable 503 "still preparing"
    // response (see hls-bootstrap-retry.ts); drives the automatic re-request.
    code?: string;
    billing?: StreamingMeditationState["billingBlocked"];
}

interface WakeLockSentinelLike {
    released?: boolean;
    release: () => Promise<void>;
}

type WakeLockNavigator = Navigator & {
    wakeLock?: {
        request: (type: "screen") => Promise<WakeLockSentinelLike>;
    };
};

type AudioTelemetrySnapshot = Pick<
    MeditationPlaybackBreakLogData,
    | "playbackType"
    | "timeSincePlaybackStartMs"
    | "timeSinceStartCalledMs"
    | "currentTime"
    | "readyState"
    | "networkState"
    | "paused"
    | "bufferedRanges"
    | "seekableEnd"
    | "duration"
>;

type AudioTelemetryReader = {
    getPlaybackTelemetrySnapshot?: () => AudioTelemetrySnapshot;
    // AIDEV-NOTE: Alerting design — stall stats reported in the playback-end
    // beacon so the server can distinguish a real unrecovered stall (interrupt)
    // from benign user abandonment.
    getStallStats?: () => {
        stallCount: number;
        maxStallMs: number;
        stalledNow: boolean;
    };
    setPlaybackSessionContext?: (
        context: MeditationPlaybackSessionContext | null,
    ) => void;
};

function appendReplayQueryParams(url: URL) {
    if (typeof window === "undefined") {
        return;
    }

    const currentUrl = new URL(window.location.href);
    const replayProviders = currentUrl.searchParams.get("replayProviders");
    const replayLatencyMultiplier = currentUrl.searchParams.get(
        "replayLatencyMultiplier",
    );

    // AIDEV-NOTE: Provider replay is a debug/test path that must follow the
    // same API surface as normal playback, so the hook forwards the current
    // page query params instead of adding a separate UI control.
    if (replayProviders === "1") {
        url.searchParams.set("replayProviders", "1");
    }

    if (replayLatencyMultiplier) {
        url.searchParams.set(
            "replayLatencyMultiplier",
            replayLatencyMultiplier,
        );
    }
}

/**
 * Hook for streaming meditation playback with intro + main audio.
 *
 * AIDEV-NOTE: Architecture overview:
 * - useReducer provides stable `dispatch` (eliminates stale closure issues)
 * - AudioController reads current state via `getState()` ref
 * - SSE events dispatch actions which trigger state transitions
 * - AudioController handles imperative audio operations
 *
 * AIDEV-NOTE: Updated for streaming intro - both intro and main now stream as chunks
 *
 * State machine flow:
 * idle -> (START) -> loading
 * loading -> (INTRO_CHUNK_RECEIVED) -> generating
 * generating -> (INTRO_COMPLETE) -> intro duration known
 * intro -> (INTRO_PLAYBACK_STARTED) -> intro playing
 * intro -> (INTRO_ENDED) -> gap
 * gap -> (GAP_ENDED) -> main (if ready) or stay in gap
 * main -> (MAIN_ENDED) -> complete
 */
export function useStreamingMeditation(
    params: StreamingMeditationParams,
): StreamingMeditationState & StreamingMeditationActions {
    const {
        type,
        voice,
        pauseMultiplier = 1,
        opening = "full",
        sessionId,
        partInteractionId,
        sessionToken,
        forceFallback,
        forceHlsJs,
        meditationId,
        streamUrl,
        experienceId,
        selectedPartIds,
    } = params;

    // AIDEV-NOTE: EVO-1954 — exercises with an experienceId ride the durable
    // HLS path like every other meditation; a bare streamUrl (no experienceId)
    // is the only remaining SSE-only case.
    const hlsEligible = !streamUrl || Boolean(experienceId);

    // AIDEV-NOTE: useReducer provides stable dispatch function that never changes identity
    const [state, dispatch] = useReducer(reducer, initialState);

    // AIDEV-NOTE: stateRef allows AudioController to read current state without stale closures
    const stateRef = useRef(state);
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    // AIDEV-NOTE: AudioController instance - created once, reads state via getState()
    const audioControllerRef = useRef<AudioController | null>(null);
    // AIDEV-NOTE: Never abort HLS bootstrap fetches; server abort cancels generation.
    const hlsBootstrapRequestIdRef = useRef(0);
    const pendingBootstrapSessionIdRef = useRef<string | null>(null);
    const pendingBootstrapPromiseRef = useRef<Promise<void> | null>(null);
    // AIDEV-NOTE: BACKEND-368 — pending backoff between "still preparing"
    // bootstrap re-requests. Cancelled (resolved early) whenever a bootstrap
    // request begins or is invalidated (stop/unmount/restart); the retry loop
    // then sees a stale request id and exits without fetching again.
    const bootstrapRetryDelayRef = useRef<{
        timer: ReturnType<typeof setTimeout>;
        resolve: () => void;
    } | null>(null);
    const playbackSessionIdRef = useRef<string | null>(null);
    const startCalledAtMsRef = useRef<number | null>(null);
    // AIDEV-NOTE: meditationId from the HLS bootstrap response — the server
    // creates the row, so fresh generations only learn their id here. Used to
    // key the playback-end beacon (initialGenerationOutcome). Cleared whenever
    // a new bootstrap request begins so a stale id never crosses sessions.
    const hlsMeditationIdRef = useRef<string | null>(null);
    // AIDEV-NOTE: End-of-playback telemetry. Tracks break count for the current
    // playback session and whether we've already reported this session ended
    // (to avoid double-send across user_stopped → unmount → pagehide).
    const playbackBreakCountRef = useRef(0);
    const playbackEndReportedRef = useRef(false);
    // AIDEV-NOTE: Count stalls fired inside the first 10s of playback startup.
    // Reset on every new playback session (via START). If >= 2, we dispatch
    // SLOW_NETWORK_DETECTED and surface a warning banner. See Gina 2026-04-14
    // (11 Mbps on Zoom) for the motivating user experience.
    const startupStallCountRef = useRef(0);
    const slowNetworkDispatchedRef = useRef(false);
    const [audiblePlaybackStartSeconds, setAudiblePlaybackStartSeconds] =
        useState(0);
    const [awaitingAudiblePlaybackStart, setAwaitingAudiblePlaybackStart] =
        useState(false);
    const wakeLockSentinelRef = useRef<WakeLockSentinelLike | null>(null);
    const wakeLockRequestInFlightRef = useRef(false);
    // AIDEV-NOTE: Browser offline detection. Shown as a "check your connection"
    // line under the buffering spinner — full offline is the only case where we
    // can confidently blame the user's connection rather than our servers.
    const [isOffline, setIsOffline] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const updateOnlineStatus = () => setIsOffline(!navigator.onLine);
        updateOnlineStatus();
        window.addEventListener("online", updateOnlineStatus);
        window.addEventListener("offline", updateOnlineStatus);
        return () => {
            window.removeEventListener("online", updateOnlineStatus);
            window.removeEventListener("offline", updateOnlineStatus);
        };
    }, []);

    const cancelBootstrapRetryDelay = useCallback(() => {
        const pendingDelay = bootstrapRetryDelayRef.current;
        if (!pendingDelay) return;
        bootstrapRetryDelayRef.current = null;
        clearTimeout(pendingDelay.timer);
        pendingDelay.resolve();
    }, []);

    const waitForBootstrapRetryDelay = useCallback(
        (delayMs: number) =>
            new Promise<void>((resolve) => {
                cancelBootstrapRetryDelay();
                const timer = setTimeout(() => {
                    if (bootstrapRetryDelayRef.current?.timer === timer) {
                        bootstrapRetryDelayRef.current = null;
                    }
                    resolve();
                }, delayMs);
                bootstrapRetryDelayRef.current = { timer, resolve };
            }),
        [cancelBootstrapRetryDelay],
    );

    const beginHlsBootstrapRequest = useCallback(() => {
        cancelBootstrapRetryDelay();
        hlsBootstrapRequestIdRef.current += 1;
        pendingBootstrapSessionIdRef.current = null;
        pendingBootstrapPromiseRef.current = null;
        hlsMeditationIdRef.current = null;
        return hlsBootstrapRequestIdRef.current;
    }, [cancelBootstrapRetryDelay]);

    const invalidateHlsBootstrapRequest = useCallback(() => {
        cancelBootstrapRetryDelay();
        hlsBootstrapRequestIdRef.current += 1;
        pendingBootstrapSessionIdRef.current = null;
        pendingBootstrapPromiseRef.current = null;
    }, [cancelBootstrapRetryDelay]);

    const releaseWakeLock = useCallback(async () => {
        const wakeLockSentinel = wakeLockSentinelRef.current;
        wakeLockSentinelRef.current = null;
        if (!wakeLockSentinel || wakeLockSentinel.released) {
            return;
        }

        try {
            await wakeLockSentinel.release();
            meditationDebugLog(
                "[useStreamingMeditation] released screen wake lock",
            );
        } catch (error) {
            meditationDebugWarn(
                "[useStreamingMeditation] failed to release screen wake lock",
                error,
            );
        }
    }, []);

    const requestWakeLock = useCallback(async () => {
        if (typeof navigator === "undefined" || document.hidden) {
            return;
        }

        const wakeLockApi = (navigator as WakeLockNavigator).wakeLock;
        if (!wakeLockApi) {
            return;
        }

        if (
            wakeLockRequestInFlightRef.current ||
            (wakeLockSentinelRef.current &&
                !wakeLockSentinelRef.current.released)
        ) {
            return;
        }

        wakeLockRequestInFlightRef.current = true;
        try {
            // AIDEV-NOTE: Wake lock is best-effort only; iOS support is partial
            // and browsers can release it on visibility changes or power events.
            wakeLockSentinelRef.current = await wakeLockApi.request("screen");
            meditationDebugLog(
                "[useStreamingMeditation] acquired screen wake lock",
            );
        } catch (error) {
            meditationDebugWarn(
                "[useStreamingMeditation] screen wake lock request failed",
                error,
            );
        } finally {
            wakeLockRequestInFlightRef.current = false;
        }
    }, []);

    const getPlaybackSessionContext =
        useCallback((): MeditationPlaybackSessionContext => {
            return {
                playbackSessionId: playbackSessionIdRef.current,
                meditationId,
                sessionId,
                partInteractionId,
                voice,
                pauseMultiplier,
                startCalledAtMs: startCalledAtMsRef.current,
            };
        }, [
            meditationId,
            partInteractionId,
            pauseMultiplier,
            sessionId,
            voice,
        ]);

    const getAudioTelemetrySnapshot =
        useCallback((): AudioTelemetrySnapshot => {
            const audio = audioControllerRef.current as
                | (AudioController & AudioTelemetryReader)
                | null;
            if (typeof audio?.getPlaybackTelemetrySnapshot === "function") {
                return audio.getPlaybackTelemetrySnapshot();
            }

            return {
                playbackType: "unknown",
                timeSincePlaybackStartMs: null,
                timeSinceStartCalledMs:
                    startCalledAtMsRef.current === null
                        ? null
                        : Date.now() - startCalledAtMsRef.current,
                currentTime: null,
                readyState: null,
                networkState: null,
                paused: null,
                bufferedRanges: [],
                seekableEnd: null,
                duration: null,
            };
        }, []);

    const syncAudioTelemetryContext = useCallback(() => {
        const audio = audioControllerRef.current as
            | (AudioController & AudioTelemetryReader)
            | null;
        if (typeof audio?.setPlaybackSessionContext === "function") {
            audio.setPlaybackSessionContext(getPlaybackSessionContext());
        }
    }, [getPlaybackSessionContext]);

    const logPlaybackBreak = useCallback(
        (
            level: "warn" | "error",
            breakType: MeditationPlaybackBreakType,
            extra: Partial<MeditationPlaybackBreakLogData> = {},
        ) => {
            const state = stateRef.current;
            const sessionContext = getPlaybackSessionContext();
            const audioTelemetry = getAudioTelemetrySnapshot();
            const payload: MeditationPlaybackBreakLogData = {
                event: "meditation.playback.break",
                breakType,
                ...sessionContext,
                phase: state.phase,
                playbackType: audioTelemetry.playbackType,
                isReconnecting: state.isReconnecting,
                timeSincePlaybackStartMs:
                    audioTelemetry.timeSincePlaybackStartMs,
                timeSinceStartCalledMs:
                    audioTelemetry.timeSinceStartCalledMs ??
                    (sessionContext.startCalledAtMs === null
                        ? null
                        : Date.now() - sessionContext.startCalledAtMs),
                currentTime: audioTelemetry.currentTime,
                readyState: audioTelemetry.readyState,
                networkState: audioTelemetry.networkState,
                paused: audioTelemetry.paused,
                bufferedRanges: audioTelemetry.bufferedRanges,
                ...extra,
            };

            playbackBreakCountRef.current += 1;

            // AIDEV-NOTE: Surface a slow-network warning to the user when they
            // hit 2+ stall breaks inside the first 10 seconds of a playback
            // session. Heuristic is conservative — one stall is noise, two
            // stalls in 10s is a reliable signal that the network can't keep
            // up with the HLS segment arrival cadence. Dispatch once per
            // playback session (guarded by ref).
            const SLOW_NETWORK_STARTUP_WINDOW_MS = 10_000;
            const SLOW_NETWORK_STALL_THRESHOLD = 2;
            const startupMs = payload.timeSinceStartCalledMs;
            if (
                breakType === "stall" &&
                !slowNetworkDispatchedRef.current &&
                typeof startupMs === "number" &&
                startupMs <= SLOW_NETWORK_STARTUP_WINDOW_MS
            ) {
                startupStallCountRef.current += 1;
                if (
                    startupStallCountRef.current >= SLOW_NETWORK_STALL_THRESHOLD
                ) {
                    slowNetworkDispatchedRef.current = true;
                    dispatch({ type: "SLOW_NETWORK_DETECTED" });
                }
            }

            if (level === "error") {
                logger.withContext(payload).error("meditation.playback.break");
                return;
            }

            logger.withContext(payload).warn("meditation.playback.break");
        },
        [getAudioTelemetrySnapshot, getPlaybackSessionContext],
    );

    // AIDEV-NOTE: Report why a playback session ended. HLS is pull-based, so
    // without this the server cannot tell natural end from tab-close / hot-reload.
    // Idempotent per playback session (guarded by playbackEndReportedRef).
    // `unload` uses sendBeacon because fetch can be cancelled during page unload.
    const reportPlaybackEnd = useCallback(
        (
            endReason:
                | "natural"
                | "user_stopped"
                | "unload"
                | "unmount"
                | "error",
            options?: { useBeacon?: boolean },
        ) => {
            if (playbackEndReportedRef.current) return;
            const playbackSessionId = playbackSessionIdRef.current;
            if (!playbackSessionId) return;
            playbackEndReportedRef.current = true;

            const state = stateRef.current;
            const audioTelemetry = getAudioTelemetrySnapshot();
            const currentTime = audioTelemetry.currentTime ?? null;
            const rawTotalDuration = getTotalDuration(state);
            // AIDEV-NOTE: On natural HLS endings the reducer-derived totalDuration
            // can be NaN/Infinity (iOS Safari reports duration=Infinity for live
            // HLS until the #EXT-X-ENDLIST is fully parsed). Fall back to the
            // <audio> element's seekableEnd, and then to its duration if finite.
            // Without this, natural endings drop totalDuration and
            // tailDeltaSeconds cannot be computed — see Case 3 in the
            // 2026-04-14 meditation staging handoff.
            const fallbackSeekableEnd =
                typeof audioTelemetry.seekableEnd === "number" &&
                Number.isFinite(audioTelemetry.seekableEnd) &&
                audioTelemetry.seekableEnd > 0
                    ? audioTelemetry.seekableEnd
                    : null;
            const fallbackDuration =
                typeof audioTelemetry.duration === "number" &&
                Number.isFinite(audioTelemetry.duration) &&
                audioTelemetry.duration > 0
                    ? audioTelemetry.duration
                    : null;
            const totalDuration =
                Number.isFinite(rawTotalDuration) && rawTotalDuration > 0
                    ? rawTotalDuration
                    : (fallbackSeekableEnd ?? fallbackDuration);
            const playedFraction =
                currentTime !== null && totalDuration !== null
                    ? Math.min(1, Math.max(0, currentTime / totalDuration))
                    : null;
            const timeSinceStartMs =
                startCalledAtMsRef.current === null
                    ? null
                    : Date.now() - startCalledAtMsRef.current;

            // AIDEV-NOTE: Alerting design — stall stats let the server tell a real
            // unrecovered stall (interrupt) from benign abandonment on unload.
            const audioReader = audioControllerRef.current as
                | (AudioController & AudioTelemetryReader)
                | null;
            const stallStats =
                typeof audioReader?.getStallStats === "function"
                    ? audioReader.getStallStats()
                    : { stallCount: 0, maxStallMs: 0, stalledNow: false };

            const payload = {
                playbackSessionId,
                endReason,
                // AIDEV-NOTE: Param id (pre-cached playback) or the id the HLS
                // bootstrap reported for this live generation.
                meditationId: meditationId ?? hlsMeditationIdRef.current,
                sessionId: sessionId ?? null,
                partInteractionId: partInteractionId ?? null,
                voice,
                type,
                pauseMultiplier,
                currentTime,
                totalDuration,
                playedFraction,
                breakCount: playbackBreakCountRef.current,
                reachedPlaying:
                    (audioTelemetry.timeSincePlaybackStartMs ?? null) !== null,
                timeSinceStartMs,
                stallCount: stallStats.stallCount,
                maxStallMs: stallStats.maxStallMs,
                stalledAtEnd: stallStats.stalledNow,
            };

            logger
                .withContext({ event: "meditation.playback.end", ...payload })
                .info("meditation.playback.end");

            if (
                typeof process !== "undefined" &&
                process.env.NODE_ENV === "test"
            ) {
                return;
            }

            const body = JSON.stringify(payload);
            const url = "/api/meditation/playback-end";
            try {
                if (
                    options?.useBeacon &&
                    typeof navigator !== "undefined" &&
                    typeof navigator.sendBeacon === "function"
                ) {
                    const blob = new Blob([body], {
                        type: "application/json",
                    });
                    navigator.sendBeacon(url, blob);
                    return;
                }
                void fetch(url, {
                    method: "POST",
                    credentials: "include",
                    keepalive: true,
                    headers: { "Content-Type": "application/json" },
                    body,
                }).catch(() => {
                    // Telemetry is best-effort; swallow network errors so they
                    // do not surface as unhandled rejections.
                });
            } catch {
                // Telemetry is best-effort. Never throw out of a teardown path.
            }
        },
        [
            getAudioTelemetrySnapshot,
            meditationId,
            partInteractionId,
            sessionId,
            type,
            pauseMultiplier,
            voice,
        ],
    );

    const dispatchTerminalPlaybackError = useCallback(
        ({
            breakType,
            message,
            billingBlocked = null,
            logFields = {},
        }: {
            breakType: MeditationPlaybackBreakType;
            message: string;
            billingBlocked?: StreamingMeditationState["billingBlocked"];
            logFields?: Partial<MeditationPlaybackBreakLogData>;
        }) => {
            // AIDEV-NOTE: Terminal playback breaks must stop active audio before
            // reducer ERROR renders, or Safari/HLS can keep playing behind the banner.
            logPlaybackBreak("error", breakType, logFields);
            reportPlaybackEnd("error");
            setAwaitingAudiblePlaybackStart(false);
            startingRef.current = false;
            isReconnectingRef.current = false;
            audioControllerRef.current?.stop();
            dispatch({
                type: "ERROR",
                message,
                billingBlocked,
            });
        },
        [logPlaybackBreak, reportPlaybackEnd],
    );

    const dispatchReconnectFailure = useCallback(
        ({
            message,
            logFields = {},
        }: {
            message: string;
            logFields?: Partial<MeditationPlaybackBreakLogData>;
        }) => {
            logPlaybackBreak("error", "sse_error", logFields);
            setAwaitingAudiblePlaybackStart(false);
            startingRef.current = false;
            isReconnectingRef.current = false;
            audioControllerRef.current?.stop();
            dispatch({
                type: "RECONNECT_FAILED",
                message,
            });
        },
        [logPlaybackBreak],
    );

    // AIDEV-NOTE: Initialize AudioController on mount with forceFallback option
    // forceFallback allows testing iOS Safari behavior on desktop browsers
    useEffect(() => {
        audioControllerRef.current = new AudioController({
            dispatch,
            getState: () => stateRef.current,
            forceFallback,
        });

        return () => {
            // AIDEV-NOTE: Component unmount with playback still active (e.g. route
            // navigation, hot reload). If no earlier endReason was reported, treat
            // as `unmount` — we're losing the audio context anyway.
            reportPlaybackEnd("unmount");
            invalidateHlsBootstrapRequest();
            void releaseWakeLock();
            audioControllerRef.current?.cleanup();
        };
    }, [
        forceFallback,
        invalidateHlsBootstrapRequest,
        releaseWakeLock,
        reportPlaybackEnd,
    ]);

    useEffect(() => {
        const handleNativeHlsAudibleStart = () => {
            // AIDEV-NOTE: Fullscreen should switch only after the controller has
            // observed raw HLS playback cross the server-provided audible threshold.
            setAudiblePlaybackStartSeconds(0);
            setAwaitingAudiblePlaybackStart(false);
        };

        window.addEventListener(
            "meditation:hls-audible-start",
            handleNativeHlsAudibleStart,
        );
        return () => {
            window.removeEventListener(
                "meditation:hls-audible-start",
                handleNativeHlsAudibleStart,
            );
        };
    }, []);

    // AIDEV-NOTE: pagehide fires on tab close, navigation, and — critically —
    // hot reload. Use navigator.sendBeacon so the request survives unload.
    // `visibilitychange:hidden` is a belt-and-suspenders fallback for iOS Safari
    // which is known to skip pagehide in some background paths.
    useEffect(() => {
        const handleUnload = () => {
            reportPlaybackEnd("unload", { useBeacon: true });
        };
        const handleVisibilityHidden = () => {
            if (typeof document !== "undefined" && document.hidden) {
                // Only report on hidden as a backup — don't race with pagehide
                // on browsers that fire both. playbackEndReportedRef guards
                // double-send either way.
                reportPlaybackEnd("unload", { useBeacon: true });
            }
        };
        window.addEventListener("pagehide", handleUnload);
        document.addEventListener("visibilitychange", handleVisibilityHidden);
        return () => {
            window.removeEventListener("pagehide", handleUnload);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityHidden,
            );
        };
    }, [reportPlaybackEnd]);

    // AIDEV-NOTE: Natural end — when the reducer transitions into `complete`
    // (MAIN_ENDED) the audio has reached its final segment. Fire once.
    useEffect(() => {
        if (state.phase === "complete") {
            reportPlaybackEnd("natural");
        }
    }, [state.phase, reportPlaybackEnd]);

    // Track if start is in progress to prevent double calls
    const startingRef = useRef(false);
    // AIDEV-NOTE: Tracks whether generate() has been called (SSE running, chunks buffering)
    // so start() knows to skip SSE connect and just unlock audio + unsuppress playback.
    const generatingRef = useRef(false);
    const isReconnectingRef = useRef(false);
    const savedPositionRef = useRef(0);
    const resetRetryCountRef = useRef<() => void>(() => {});

    // AIDEV-NOTE: Handle SSE events - dispatch state changes and trigger audio actions
    // Updated for streaming intro: intro_chunk, intro_complete replace intro_url
    const handleSSEEvent = useCallback(
        (event: SSEEvent) => {
            const audio = audioControllerRef.current;
            if (!audio) {
                return;
            }

            // AIDEV-NOTE: Reconnects re-request the same SSE URL, but only the cached
            // complete event matters on the second pass. Ignore chunk/metadata noise
            // and resume playback from the saved position once the signed URL arrives.
            if (isReconnectingRef.current) {
                if (event.type === "complete") {
                    if (!event.audioUrl) {
                        isReconnectingRef.current = false;
                        dispatchReconnectFailure({
                            message: "Connection lost. Please try again.",
                            logFields: {
                                errorMessage:
                                    "Reconnect completed without a cached audio URL.",
                            },
                        });
                        return;
                    }

                    audio.resumeFromUrl(
                        event.audioUrl,
                        savedPositionRef.current,
                    );
                    dispatch({ type: "RECONNECT_SUCCESS" });
                    isReconnectingRef.current = false;
                    resetRetryCountRef.current();
                    return;
                }

                if (event.type === "error") {
                    isReconnectingRef.current = false;
                    dispatchReconnectFailure({
                        message: "Connection lost. Please try again.",
                        logFields: {
                            errorMessage: event.message,
                        },
                    });
                    return;
                }

                return;
            }

            switch (event.type) {
                case "intro_chunk": {
                    dispatch({ type: "INTRO_CHUNK_RECEIVED" });
                    const chunkData = Uint8Array.from(atob(event.data), (c) =>
                        c.charCodeAt(0),
                    );
                    audio.appendIntroChunk(chunkData);
                    break;
                }

                case "intro_complete":
                    dispatch({
                        type: "INTRO_COMPLETE",
                        duration: event.duration,
                    });
                    // Create blob and start playback immediately
                    audio.finalizeIntroStream();
                    break;

                case "audio_chunk": {
                    dispatch({ type: "CHUNK_RECEIVED" });
                    const chunkData = Uint8Array.from(atob(event.data), (c) =>
                        c.charCodeAt(0),
                    );
                    audio.appendChunk(chunkData);
                    break;
                }

                case "complete":
                    {
                        // AIDEV-NOTE: Instrument event ordering for the
                        // stuck-spinner bug — we want to see whether
                        // GENERATION_COMPLETE arrives before or after the
                        // audio element's MAIN_ENDED. If MAIN_ENDED already
                        // fired (phase === "complete") when this event
                        // arrives, the spinner cleared late. If it hasn't
                        // fired, the happy path is working.
                        const stateAtComplete = stateRef.current;
                        logger
                            .withContext({
                                event: "meditation.generation_complete.dispatch",
                                phase: stateAtComplete.phase,
                                isGenerating: stateAtComplete.isGenerating,
                                mainEndedAlready:
                                    stateAtComplete.phase === "complete",
                                totalDuration: event.totalDuration,
                            })
                            .info("meditation.generation_complete.dispatch");
                        dispatch({
                            type: "GENERATION_COMPLETE",
                            totalDuration: event.totalDuration,
                        });
                    }

                    // AIDEV-NOTE: Store audioUrl for fallback if MediaSource can't decode cached format
                    audio.setAudioUrl(event.audioUrl);

                    // AIDEV-NOTE: Use URL-based playback when:
                    // - Fallback mode (iOS Safari without MediaSource)
                    // - No chunks received (server detected cached MP3, skipped chunk streaming)
                    const useFallbackPath =
                        (audio.fallbackMode || !audio.receivedMainChunks) &&
                        (event.audioUrl || !audio.receivedMainChunks);

                    // AIDEV-NOTE: Log which playback path is taken on complete for iOS diagnosis
                    meditationDebugLog(
                        "[StreamingMeditation] complete event: choosing playback path",
                        {
                            useFallbackPath,
                            fallbackMode: audio.fallbackMode,
                            receivedMainChunks: audio.receivedMainChunks,
                            hasAudioUrl: !!event.audioUrl,
                            totalDuration: event.totalDuration,
                            phase: stateRef.current.phase,
                        },
                    );

                    if (useFallbackPath) {
                        // AIDEV-NOTE: Prefer the server-provided signed URL for legacy cached files.
                        // Only fall back to /api/meditation/audio if the stream completed without
                        // chunks and without a usable direct URL.
                        const playbackUrl =
                            event.audioUrl ??
                            (!audio.receivedMainChunks
                                ? buildDirectAudioUrl()
                                : undefined);
                        if (playbackUrl) {
                            audio.playFromUrl(playbackUrl);
                        }
                    } else {
                        audio.finalizeStream();

                        // If in gap phase, try to play main
                        if (stateRef.current.phase === "gap") {
                            audio.tryPlayMain();
                        }
                    }
                    break;

                case "error":
                    dispatchTerminalPlaybackError({
                        breakType: "sse_error",
                        message: event.message,
                        logFields: {
                            errorMessage: event.message,
                        },
                    });
                    break;

                case "metadata":
                    dispatch({ type: "SET_SOURCE", source: event.source });
                    break;
            }
        },
        [dispatchReconnectFailure, dispatchTerminalPlaybackError],
    );

    // AIDEV-NOTE: Handle SSE errors
    const handleSSEError = useCallback(
        (error: Error) => {
            isReconnectingRef.current = false;

            if (stateRef.current.isGenerating) {
                dispatchTerminalPlaybackError({
                    breakType: "sse_error",
                    message: "Generation interrupted — please start again.",
                    billingBlocked: isBillingLlmBlockedError(error)
                        ? error.billing
                        : null,
                    logFields: {
                        errorName: error.name,
                        errorMessage: error.message,
                    },
                });
                startingRef.current = false;
                return;
            }

            dispatchTerminalPlaybackError({
                breakType: "sse_error",
                message: error.message,
                billingBlocked: isBillingLlmBlockedError(error)
                    ? error.billing
                    : null,
                logFields: {
                    errorName: error.name,
                    errorMessage: error.message,
                },
            });
            startingRef.current = false;
        },
        [dispatchTerminalPlaybackError],
    );

    // Build SSE URL
    // AIDEV-NOTE: Input streaming enabled by default for faster time-to-first-audio
    // (ElevenLabs incremental batch: LLM paragraphs are voiced as they complete)
    // AIDEV-NOTE: When meditationId is provided, the endpoint streams the cached file directly
    const buildSSEUrl = useCallback(() => {
        // AIDEV-NOTE: When streamUrl is provided (e.g. exercise streaming), use it directly
        if (streamUrl) {
            const url = new URL(streamUrl, window.location.origin);
            if (pauseMultiplier > 1) {
                url.searchParams.set(
                    "pauseMultiplier",
                    String(pauseMultiplier),
                );
            }
            if (!experienceId && opening === "brief") {
                url.searchParams.set("opening", opening);
            }
            return url.toString();
        }

        const url = new URL("/api/meditation/stream", window.location.origin);

        // AIDEV-NOTE: When meditationId is provided, the endpoint uses it to look up the cached file
        // No need to send type/voice/partInteractionId - the endpoint gets them from the meditation record
        // AIDEV-NOTE: sessionId MUST still be passed so the auth middleware can validate the sessionToken
        if (meditationId) {
            url.searchParams.set("meditationId", meditationId);
            if (sessionId) url.searchParams.set("sessionId", sessionId);
            if (sessionToken)
                url.searchParams.set("sessionToken", sessionToken);
            appendReplayQueryParams(url);
            return url.toString();
        }

        url.searchParams.set("type", type);
        url.searchParams.set("voice", voice);
        if (pauseMultiplier > 1) {
            url.searchParams.set("pauseMultiplier", String(pauseMultiplier));
        }
        if (opening === "brief") {
            url.searchParams.set("opening", opening);
        }
        url.searchParams.set("inputStreaming", "1"); // Enable input streaming
        if (sessionId) url.searchParams.set("sessionId", sessionId);
        if (partInteractionId)
            url.searchParams.set("partInteractionId", partInteractionId);
        if (sessionToken) url.searchParams.set("sessionToken", sessionToken);
        appendReplayQueryParams(url);
        return url.toString();
    }, [
        type,
        voice,
        sessionId,
        partInteractionId,
        sessionToken,
        meditationId,
        streamUrl,
        pauseMultiplier,
        opening,
        experienceId,
    ]);

    // Build direct audio streaming URL (fallback for iOS Safari)
    // AIDEV-NOTE: This endpoint returns raw audio bytes with chunked transfer encoding
    // Safari can progressively play this without MediaSource API
    const buildDirectAudioUrl = useCallback(() => {
        const url = new URL("/api/meditation/audio", window.location.origin);
        if (meditationId) {
            url.searchParams.set("meditationId", meditationId);
            if (sessionId) url.searchParams.set("sessionId", sessionId);
            if (sessionToken)
                url.searchParams.set("sessionToken", sessionToken);
            appendReplayQueryParams(url);
            return url.toString();
        }
        url.searchParams.set("type", type);
        url.searchParams.set("voice", voice);
        if (pauseMultiplier > 1) {
            url.searchParams.set("pauseMultiplier", String(pauseMultiplier));
        }
        if (opening === "brief") {
            url.searchParams.set("opening", opening);
        }
        if (sessionId) url.searchParams.set("sessionId", sessionId);
        if (partInteractionId)
            url.searchParams.set("partInteractionId", partInteractionId);
        if (sessionToken) url.searchParams.set("sessionToken", sessionToken);
        appendReplayQueryParams(url);
        return url.toString();
    }, [
        type,
        voice,
        pauseMultiplier,
        opening,
        sessionId,
        partInteractionId,
        sessionToken,
        meditationId,
    ]);

    const buildHlsBootstrapUrl = useCallback(() => {
        const url = new URL("/api/meditation/hls", window.location.origin);
        // AIDEV-NOTE: ALWAYS send the voice, before the branches. The meditationId branch
        // below returns early and used to omit it, and /api/meditation/hls defaults a
        // missing voice to "Brad" — so an exercise (which carries BOTH ids, so that branch
        // wins) was generated in Brad's voice no matter what the user picked. The storage
        // path still said "Tamala", because that comes from the Inngest event rather than
        // this URL, which is exactly why it looked like a voice-map bug for so long.
        url.searchParams.set("voice", voice);
        if (meditationId) {
            url.searchParams.set("meditationId", meditationId);
            if (sessionId) url.searchParams.set("sessionId", sessionId);
            if (sessionToken)
                url.searchParams.set("sessionToken", sessionToken);
            appendReplayQueryParams(url);
            return url.toString();
        }
        // AIDEV-NOTE: EVO-1954 — exercises bootstrap the same durable HLS path;
        // the server resolves the exercise prompt from these ids.
        if (experienceId) {
            url.searchParams.set("experienceId", experienceId);
            if (pauseMultiplier > 1) {
                url.searchParams.set(
                    "pauseMultiplier",
                    String(pauseMultiplier),
                );
            }
            // Resource exercises use canonical scripts; opening only applies
            // to generated meditations without an experienceId.
            if (selectedPartIds?.length) {
                url.searchParams.set(
                    "selectedPartIds",
                    selectedPartIds.join(","),
                );
            }
            // cubic P3 — keep the replay/debug contract consistent with the
            // other playback paths.
            appendReplayQueryParams(url);
            return url.toString();
        }
        url.searchParams.set("type", type);
        url.searchParams.set("voice", voice);
        if (pauseMultiplier > 1) {
            url.searchParams.set("pauseMultiplier", String(pauseMultiplier));
        }
        if (opening === "brief") {
            url.searchParams.set("opening", opening);
        }
        if (sessionId) url.searchParams.set("sessionId", sessionId);
        if (partInteractionId)
            url.searchParams.set("partInteractionId", partInteractionId);
        if (sessionToken) url.searchParams.set("sessionToken", sessionToken);
        appendReplayQueryParams(url);
        return url.toString();
    }, [
        type,
        voice,
        sessionId,
        partInteractionId,
        sessionToken,
        meditationId,
        experienceId,
        selectedPartIds,
        pauseMultiplier,
        opening,
    ]);

    const bootstrapHlsPlayback = useCallback(
        async (audio: AudioController) => {
            const currentPlaybackSessionId = playbackSessionIdRef.current;
            const pendingBootstrapPromise = pendingBootstrapPromiseRef.current;

            if (pendingBootstrapPromise) {
                const pendingPlaybackSessionId =
                    pendingBootstrapSessionIdRef.current;
                const isDuplicateBootstrap =
                    currentPlaybackSessionId !== null &&
                    pendingPlaybackSessionId === currentPlaybackSessionId;

                if (isDuplicateBootstrap) {
                    logPlaybackBreak("warn", "bootstrap_duplicate", {
                        errorMessage:
                            "bootstrapHlsPlayback reused the in-flight bootstrap request for this playback session.",
                        priorPlaybackSessionId: pendingPlaybackSessionId,
                    });
                    return pendingBootstrapPromise;
                }

                logPlaybackBreak("warn", "bootstrap_duplicate", {
                    errorMessage:
                        "bootstrapHlsPlayback invalidating a stale bootstrap request for a different playback session.",
                    priorPlaybackSessionId: pendingPlaybackSessionId,
                });
                audio.stop();
                setAwaitingAudiblePlaybackStart(false);
            }

            if (stateRef.current.isPlaying) {
                logPlaybackBreak("warn", "bootstrap_duplicate", {
                    alreadyPlaying: true,
                    errorMessage:
                        "bootstrapHlsPlayback invoked while playback was already marked playing.",
                });
            }

            const bootstrapUrl = buildHlsBootstrapUrl();
            setAwaitingAudiblePlaybackStart(true);
            meditationDebugLog(
                "[useStreamingMeditation] requesting HLS bootstrap",
                {
                    bootstrapUrl: bootstrapUrl.substring(0, 100),
                    forceHlsJs: Boolean(forceHlsJs),
                    pauseMultiplier,
                },
            );
            dispatch({ type: "CHUNK_RECEIVED" });
            const bootstrapRequestId = beginHlsBootstrapRequest();
            const isCurrentBootstrapRequest = () =>
                hlsBootstrapRequestIdRef.current === bootstrapRequestId;
            pendingBootstrapSessionIdRef.current = playbackSessionIdRef.current;

            let resolvePendingBootstrap = () => {};
            const bootstrapPromise = new Promise<void>((resolve) => {
                resolvePendingBootstrap = resolve;
            });
            pendingBootstrapPromiseRef.current = bootstrapPromise;

            try {
                // AIDEV-NOTE: BACKEND-368 — the server 503s with a "still
                // preparing" code after ~30s when a slow/silent first Inngest
                // attempt hasn't primed the stream yet. A repeat request reuses
                // that in-progress generation (server-verified), so re-request
                // up to MEDITATION_HLS_BOOTSTRAP_RETRY_DELAYS_MS.length times,
                // keeping the loading UI. Any other non-OK stays terminal.
                let response: Response;
                let payload: HlsBootstrapPayload | null;
                for (let retryIndex = 0; ; retryIndex += 1) {
                    response = await fetch(bootstrapUrl);
                    payload =
                        ((await response
                            .json()
                            .catch(
                                () => null,
                            )) as HlsBootstrapPayload | null) ?? null;

                    if (!isCurrentBootstrapRequest()) {
                        return;
                    }

                    const retryDelayMs =
                        MEDITATION_HLS_BOOTSTRAP_RETRY_DELAYS_MS[retryIndex];
                    if (
                        retryDelayMs === undefined ||
                        !isRetryableHlsBootstrapResponse(
                            response.status,
                            payload,
                        )
                    ) {
                        break;
                    }

                    meditationDebugLog(
                        "[useStreamingMeditation] HLS bootstrap still preparing; retrying",
                        { retryAttempt: retryIndex + 1, retryDelayMs },
                    );
                    await waitForBootstrapRetryDelay(retryDelayMs);
                    if (!isCurrentBootstrapRequest()) {
                        return;
                    }
                }

                if (!response.ok) {
                    dispatchTerminalPlaybackError({
                        breakType: "bootstrap_http_error",
                        message:
                            payload?.message ??
                            "Failed to prepare audio. Please try again.",
                        billingBlocked: payload?.billing ?? null,
                        logFields: {
                            httpStatus: response.status,
                            responseMessage: payload?.message,
                            errorMessage: response.statusText,
                        },
                    });
                    return;
                }

                if (!payload?.playbackUrl) {
                    dispatchTerminalPlaybackError({
                        breakType: "bootstrap_missing_url",
                        message:
                            payload?.message ??
                            "Failed to prepare audio. Please try again.",
                        billingBlocked: payload?.billing ?? null,
                        logFields: {
                            responseMessage: payload?.message,
                            errorMessage:
                                "HLS bootstrap returned OK without a playbackUrl.",
                        },
                    });
                    return;
                }

                hlsMeditationIdRef.current = payload.meditationId ?? null;

                meditationDebugLog(
                    "[useStreamingMeditation] HLS bootstrap response received",
                    {
                        playbackType: payload.playbackType,
                        playbackUrl: payload.playbackUrl.substring(0, 120),
                        audibleStartOffsetSeconds:
                            payload.audibleStartOffsetSeconds ?? 0,
                        forceHlsJs: Boolean(forceHlsJs),
                        pauseMultiplier,
                    },
                );

                if (payload.playbackType === "hls") {
                    setAwaitingAudiblePlaybackStart(
                        (payload.audibleStartOffsetSeconds ?? 0) > 0,
                    );
                    setAudiblePlaybackStartSeconds(
                        payload.audibleStartOffsetSeconds ?? 0,
                    );
                    audio.startHlsPlayback(
                        payload.playbackUrl,
                        payload.audibleStartOffsetSeconds ?? 0,
                        { forceHlsJs: Boolean(forceHlsJs) },
                    );
                } else {
                    setAwaitingAudiblePlaybackStart(false);
                    setAudiblePlaybackStartSeconds(0);
                    audio.playFromUrl(payload.playbackUrl);
                }
            } catch (error) {
                if (!isCurrentBootstrapRequest()) {
                    return;
                }

                console.error(
                    "[useStreamingMeditation] fallback bootstrap failed",
                    error,
                );
                dispatchTerminalPlaybackError({
                    breakType: "bootstrap_network_error",
                    message: "Failed to prepare audio. Please try again.",
                    logFields: {
                        errorName:
                            error instanceof Error ? error.name : undefined,
                        errorMessage:
                            error instanceof Error
                                ? error.message
                                : "Unknown bootstrap fetch failure",
                    },
                });
            } finally {
                if (pendingBootstrapPromiseRef.current === bootstrapPromise) {
                    pendingBootstrapPromiseRef.current = null;
                    pendingBootstrapSessionIdRef.current = null;
                }
                resolvePendingBootstrap();
                setTimeout(() => {
                    startingRef.current = false;
                }, 500);
            }
        },
        [
            beginHlsBootstrapRequest,
            buildHlsBootstrapUrl,
            dispatchTerminalPlaybackError,
            forceHlsJs,
            logPlaybackBreak,
            waitForBootstrapRetryDelay,
        ],
    );

    // SSE connection hook
    const {
        connect: connectSSE,
        close: closeSSE,
        resetRetryCount,
    } = useSSEConnection({
        url: buildSSEUrl(),
        onEvent: handleSSEEvent,
        onError: handleSSEError,
        reconnect: {
            enabled: true,
            maxRetries: 3,
            onReconnecting: () => {
                const audio = audioControllerRef.current;
                if (audio) {
                    savedPositionRef.current = audio.getCurrentMainTime();
                }
                isReconnectingRef.current = true;
                dispatch({ type: "RECONNECTING" });
            },
            onReconnectFailed: () => {
                isReconnectingRef.current = false;
                dispatchReconnectFailure({
                    message: "Connection lost. Please try again.",
                    logFields: {
                        errorMessage:
                            "SSE reconnect attempts exhausted before playback recovered.",
                    },
                });
            },
            shouldReconnect: () => {
                const currentState = stateRef.current;
                return (
                    currentState.phase === "main" ||
                    currentState.phase === "intro" ||
                    currentState.phase === "gap"
                );
            },
        },
    });
    resetRetryCountRef.current = resetRetryCount;

    // AIDEV-NOTE: No automatic SSE cleanup effect
    // SSE connections complete naturally when the stream ends, or are explicitly
    // stopped via stop(). Automatic cleanup causes issues with React Strict Mode
    // double-invocation where the cleanup aborts active connections during the
    // simulated unmount/remount cycle.

    useEffect(() => {
        const meditationNeedsWakeLock =
            state.isLoading || state.isGenerating || state.isPlaying;

        if (meditationNeedsWakeLock) {
            void requestWakeLock();
            return;
        }

        void releaseWakeLock();
    }, [
        releaseWakeLock,
        requestWakeLock,
        state.isGenerating,
        state.isLoading,
        state.isPlaying,
    ]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                wakeLockSentinelRef.current = null;
                return;
            }

            const currentState = stateRef.current;
            const meditationNeedsWakeLock =
                currentState.isLoading ||
                currentState.isGenerating ||
                currentState.isPlaying;

            if (meditationNeedsWakeLock) {
                void requestWakeLock();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
        };
    }, [requestWakeLock]);

    // AIDEV-NOTE: Cached HLS bootstrap payload from generate(). When the user is
    // on Safari/HLS, generate() pre-fetches the bootstrap URL so start() can skip
    // the network request and begin playback immediately.
    const hlsBootstrapCacheRef = useRef<HlsBootstrapPayload | null>(null);

    // AIDEV-NOTE: EVO-1815 — terminal errors must fully release the start guards.
    // AudioController.failPlayback() dispatches ERROR without going through
    // dispatchTerminalPlaybackError, so a stale generatingRef would make the
    // next Retry tap take the pre-generate branch and silently no-op on dead
    // audio. Clearing here (single choke point on state.error) covers every
    // ERROR/RECONNECT_FAILED source.
    useEffect(() => {
        if (!state.error) {
            return;
        }
        startingRef.current = false;
        generatingRef.current = false;
        isReconnectingRef.current = false;
        hlsBootstrapCacheRef.current = null;
    }, [state.error]);

    // AIDEV-NOTE: Start generation in the background without audio playback.
    // On Chrome: SSE chunks buffer into MediaSource silently.
    // On Safari: HLS bootstrap endpoint is pre-fetched (triggers server-side generation).
    // In both cases, the user can type their intention while audio generates.
    // Call start() later (in a user gesture) to unlock audio and begin playback.
    const generate = useCallback(() => {
        if (generatingRef.current || startingRef.current) return;

        const audio = audioControllerRef.current;
        if (!audio) return;

        // AIDEV-NOTE: EVO-1954 — HLS is the unified transport; exercises with
        // an experienceId bootstrap it too. Only bare-streamUrl callers stay SSE.
        const shouldPreferHls =
            hlsEligible && (forceHlsJs || audio.shouldPreferHlsPlaybackPath());

        if (shouldPreferHls) {
            // AIDEV-NOTE: Can't silently buffer HLS via MediaSource. Instead,
            // pre-fetch the HLS bootstrap endpoint in the background. This triggers
            // server-side meditation generation so the playback URL is ready when
            // start() is called. If the fetch hasn't resolved by then, start() will
            // do a fresh bootstrap (still faster since generation already started).
            dispatch({ type: "START" });
            generatingRef.current = true;
            hlsBootstrapCacheRef.current = null;

            const bootstrapUrl = buildHlsBootstrapUrl();
            const bootstrapRequestId = beginHlsBootstrapRequest();
            const isCurrentBootstrapRequest = () =>
                hlsBootstrapRequestIdRef.current === bootstrapRequestId;

            meditationDebugLog(
                "[useStreamingMeditation] generate: pre-fetching HLS bootstrap",
                { bootstrapUrl: bootstrapUrl.substring(0, 100) },
            );

            fetch(bootstrapUrl)
                .then(async (response) => {
                    const payload =
                        ((await response
                            .json()
                            .catch(
                                () => null,
                            )) as HlsBootstrapPayload | null) ?? null;

                    if (!isCurrentBootstrapRequest()) return;

                    if (response.ok && payload?.playbackUrl) {
                        hlsBootstrapCacheRef.current = payload;
                        hlsMeditationIdRef.current =
                            payload.meditationId ?? null;
                        dispatch({ type: "CHUNK_RECEIVED" });
                        meditationDebugLog(
                            "[useStreamingMeditation] generate: HLS bootstrap ready",
                            { playbackType: payload.playbackType },
                        );
                    }
                    // If fetch failed, start() will retry via fresh bootstrapHlsPlayback
                })
                .catch(() => {
                    // Silently fail — start() will do a fresh bootstrap
                });

            return;
        }

        // MediaSource path — buffer chunks silently
        dispatch({ type: "START" });
        resetRetryCountRef.current();
        isReconnectingRef.current = false;

        audio.stop(); // Clean up any previous state
        const result = audio.initializeMediaSource();

        // AIDEV-NOTE: In fallback mode (no MediaSource), we can't buffer chunks
        // silently. generate() is a no-op; start() will handle everything.
        if (result.fallbackMode) {
            return;
        }

        // Suppress playback so chunks buffer without playing
        audio.suppressPlayback();
        generatingRef.current = true;

        // Connect SSE to start server-side generation
        connectSSE();
    }, [
        beginHlsBootstrapRequest,
        buildHlsBootstrapUrl,
        connectSSE,
        forceHlsJs,
    ]);

    // AIDEV-NOTE: Start streaming - dispatch action, init audio, connect SSE.
    // If generate() was already called, this just unlocks audio and unsuppresses
    // playback of the already-buffering content. Otherwise, full start from scratch.
    // In fallback mode (iOS Safari), always does a full start with direct streaming.
    const start = useCallback(async () => {
        const currentState = stateRef.current;
        const hasActiveMeditation =
            currentState.isLoading ||
            currentState.isGenerating ||
            currentState.isPlaying ||
            currentState.phase === "intro" ||
            currentState.phase === "gap" ||
            currentState.phase === "main";

        meditationVerboseDebugLog("[useStreamingMeditation] start() called", {
            type,
            voice,
            pauseMultiplier,
            sessionId,
            partInteractionId,
            meditationId,
            alreadyStarting: startingRef.current,
            phase: currentState.phase,
            isLoading: currentState.isLoading,
            isGenerating: currentState.isGenerating,
            isPlaying: currentState.isPlaying,
            preGenerating: generatingRef.current,
        });
        // Prevent double start calls
        if (startingRef.current) {
            meditationVerboseDebugLog(
                "[useStreamingMeditation] start() blocked — already starting",
            );
            return;
        }

        const audio = audioControllerRef.current;

        // AIDEV-NOTE: If generate() was called, we're pre-generating in the background.
        // This check must come BEFORE hasActiveMeditation because generate() sets
        // isLoading/isGenerating which would otherwise block start().
        if (generatingRef.current && audio) {
            startingRef.current = true;
            setAudiblePlaybackStartSeconds(0);
            setAwaitingAudiblePlaybackStart(false);

            // AIDEV-NOTE: Skip unlockAudioForMobile() in the pre-generate path.
            // start() runs in user-gesture context (Skip/Begin click) so the first
            // play() already has autoplay permission. The unlock's silent data URI
            // would conflict with hls.js attachMedia() on the same element, and in
            // Chromium the data URI play() hangs indefinitely.

            const cachedBootstrap = hlsBootstrapCacheRef.current;
            hlsBootstrapCacheRef.current = null;
            generatingRef.current = false;

            if (cachedBootstrap) {
                // AIDEV-NOTE: HLS pre-fetch completed — start playback with cached URL.
                // No additional network request needed.
                meditationDebugLog(
                    "[useStreamingMeditation] start: using pre-fetched HLS bootstrap",
                    { playbackType: cachedBootstrap.playbackType },
                );
                setAwaitingAudiblePlaybackStart(true);
                dispatch({ type: "CHUNK_RECEIVED" });

                if (cachedBootstrap.playbackType === "hls") {
                    setAwaitingAudiblePlaybackStart(
                        (cachedBootstrap.audibleStartOffsetSeconds ?? 0) > 0,
                    );
                    setAudiblePlaybackStartSeconds(
                        cachedBootstrap.audibleStartOffsetSeconds ?? 0,
                    );
                    audio.startHlsPlayback(
                        cachedBootstrap.playbackUrl,
                        cachedBootstrap.audibleStartOffsetSeconds ?? 0,
                        { forceHlsJs: Boolean(forceHlsJs) },
                    );
                } else {
                    setAwaitingAudiblePlaybackStart(false);
                    setAudiblePlaybackStartSeconds(0);
                    audio.playFromUrl(cachedBootstrap.playbackUrl);
                }
                setTimeout(() => {
                    startingRef.current = false;
                }, 500);
                return;
            }

            if (
                hlsEligible &&
                (forceHlsJs || audio.shouldPreferHlsPlaybackPath())
            ) {
                // AIDEV-NOTE: Stale HLS fetches are invalidated locally, not aborted.
                audio.stop();
                invalidateHlsBootstrapRequest();
                resetRetryCountRef.current();
                isReconnectingRef.current = false;
                await bootstrapHlsPlayback(audio);
                return;
            }

            // MediaSource path — chunks are buffered, just unsuppress playback
            audio.unsuppressPlayback();
            setTimeout(() => {
                startingRef.current = false;
            }, 500);
            return;
        }

        // AIDEV-NOTE: Keep the user-gesture start path single-flight for the full
        // loading/generating/playback window. A second tap during fallback HLS
        // startup can poison the shared audio element and spawn duplicate sessions.
        if (hasActiveMeditation) {
            meditationVerboseDebugLog(
                "[useStreamingMeditation] start() blocked — meditation already active",
                {
                    phase: currentState.phase,
                    isLoading: currentState.isLoading,
                    isGenerating: currentState.isGenerating,
                    isPlaying: currentState.isPlaying,
                },
            );
            return;
        }
        startingRef.current = true;
        playbackSessionIdRef.current = crypto.randomUUID();
        startCalledAtMsRef.current = Date.now();
        playbackBreakCountRef.current = 0;
        playbackEndReportedRef.current = false;
        startupStallCountRef.current = 0;
        slowNetworkDispatchedRef.current = false;
        logger
            .withContext({
                event: "meditation.playback.start",
                playbackSessionId: playbackSessionIdRef.current,
                meditationId,
                sessionId,
                partInteractionId,
                voice,
                forceHlsJs: Boolean(forceHlsJs),
            })
            .info("meditation.playback.start");
        setAudiblePlaybackStartSeconds(0);
        setAwaitingAudiblePlaybackStart(false);

        // AIDEV-NOTE: If generate() already started SSE and is buffering chunks,
        // we just need to unlock audio (user gesture context) and unsuppress playback.
        // No need to reinitialize MediaSource or reconnect SSE.
        if (generatingRef.current && audio) {
            const unlockPromise = audio.unlockAudioForMobile();
            if (unlockPromise) {
                await unlockPromise;
            }
            audio.unsuppressPlayback();
            generatingRef.current = false;
            setTimeout(() => {
                startingRef.current = false;
            }, 500);
            return;
        }

        // AIDEV-NOTE: Fresh starts must reset the reconnect budget so a prior
        // exhausted session doesn't disable auto-reconnect for the next run.
        resetRetryCountRef.current();
        isReconnectingRef.current = false;
        savedPositionRef.current = 0;

        let unlockPromise: Promise<void> | undefined;

        // AIDEV-NOTE: Unlock audio element FIRST, synchronously in user gesture.
        // The play() call happens synchronously here (in gesture context).
        // We await the returned promise to ensure the unlock settles before
        // stop() would interrupt it. This prevents the NotAllowedError race.
        if (audio) {
            unlockPromise = audio.unlockAudioForMobile();
        }

        // Reset state
        dispatch({ type: "START" });

        // Initialize for streaming (or fallback mode for iOS Safari)
        if (audio) {
            if (unlockPromise) {
                meditationVerboseDebugLog(
                    "[useStreamingMeditation] awaiting unlock promise...",
                );
                await unlockPromise;
                meditationVerboseDebugLog(
                    "[useStreamingMeditation] unlock promise settled",
                );
            }
            audio.stop(); // Clean up any previous state
            syncAudioTelemetryContext();
            const shouldPreferHlsPlaybackPath =
                hlsEligible &&
                (forceHlsJs || audio.shouldPreferHlsPlaybackPath());
            if (shouldPreferHlsPlaybackPath) {
                await bootstrapHlsPlayback(audio);
                return;
            }
            const result = audio.initializeMediaSource();
            meditationVerboseDebugLog(
                "[useStreamingMeditation] initializeMediaSource result",
                result,
            );

            // AIDEV-NOTE: Safari fallback now bootstraps native HLS instead of
            // direct chunked audio because HLS startup is more reliable on iOS.
            // Bare-streamUrl callers skip HLS and fall through to SSE.
            if (result.fallbackMode && hlsEligible) {
                await bootstrapHlsPlayback(audio);
                return; // Skip SSE connection in fallback mode
            }
        }

        // Connect to SSE (only for browsers with MediaSource support)
        connectSSE();

        // Allow future starts after a short delay
        setTimeout(() => {
            startingRef.current = false;
        }, 500);
    }, [
        bootstrapHlsPlayback,
        forceHlsJs,
        invalidateHlsBootstrapRequest,
        meditationId,
        partInteractionId,
        sessionId,
        connectSSE,
        type,
        voice,
    ]);

    // Play control
    const play = useCallback(() => {
        dispatch({ type: "PLAY" });
        audioControllerRef.current?.play();
    }, []);

    // Pause control
    const pause = useCallback(() => {
        dispatch({ type: "PAUSE" });
        audioControllerRef.current?.pause();
    }, []);

    // Stop control
    const stop = useCallback(() => {
        reportPlaybackEnd("user_stopped");
        dispatch({ type: "STOP" });
        closeSSE();
        audioControllerRef.current?.stop();
        invalidateHlsBootstrapRequest();
        setAudiblePlaybackStartSeconds(0);
        setAwaitingAudiblePlaybackStart(false);
        startingRef.current = false;
        generatingRef.current = false;
        hlsBootstrapCacheRef.current = null;
        isReconnectingRef.current = false;
        savedPositionRef.current = 0;
        void releaseWakeLock();
    }, [
        closeSSE,
        invalidateHlsBootstrapRequest,
        releaseWakeLock,
        reportPlaybackEnd,
    ]);

    // Seek control
    const seek = useCallback((time: number) => {
        dispatch({ type: "SEEK", time });
        audioControllerRef.current?.seek(time);
    }, []);

    const stableTotalDuration =
        state.mainDuration > 0
            ? getTotalDuration(state)
            : state.introDuration > 0 && state.phase === "intro"
              ? state.introDuration
              : 0;

    // Build return value
    const returnState: StreamingMeditationState = {
        activeMeditationId: meditationId ?? hlsMeditationIdRef.current ?? null,
        phase: state.phase,
        isPlaying: state.isPlaying,
        isLoading: state.isLoading,
        isGenerating: state.isGenerating,
        error: state.error,
        billingBlocked: state.billingBlocked,
        currentTime: state.currentTime,
        // AIDEV-NOTE: Keep the UI-facing total duration stable until the server
        // finalizes main duration; HLS manifest durations drift while streaming.
        totalDuration: stableTotalDuration,
        introDuration: state.introDuration,
        mainDuration: state.mainDuration,
        audiblePlaybackStartSeconds,
        awaitingAudiblePlaybackStart,
        statusLabel: getStatusLabel(state),
        source: state.source,
        slowNetworkDetected: state.slowNetworkDetected,
        needsUserGesture: state.needsUserGesture,
        isStalled: state.isStalled,
        isOffline,
    };

    // AIDEV-NOTE: resumePlayback is called from a fresh user gesture (button tap)
    // after NotAllowedError. Delegates to AudioController.resumeWithUserGesture()
    // which calls play() on the already-buffered audio element.
    const resumePlayback = useCallback(() => {
        audioControllerRef.current?.resumeWithUserGesture();
    }, []);

    const actions: StreamingMeditationActions = {
        generate,
        start,
        play,
        pause,
        stop,
        seek,
        resumePlayback,
    };

    return { ...returnState, ...actions };
}
